/**
 * Shopify Chat Widget Position + Visibility Fix
 *
 * Responsibilities:
 *   1. Force the widget to bottom-left so it doesn't collide with UI
 *   2. Lift above footer on mobile
 *   3. When a drawer is open (cart / nav-finder), STOP forcing visibility so the
 *      drawer stylesheets (display:none !important) win
 *   4. When no drawer is open, force visibility so the widget can't hide itself
 *
 * History: an earlier version observed the inbox's own `style` attribute. That
 * created a feedback loop — every setProperty triggered the observer, which
 * called setProperty again, which… saturated the main thread and crashed the
 * widget (NotSupportedError: attachShadow on host that already has shadow). We
 * now react ONLY to external triggers: widget added to DOM, body class change,
 * resize, scroll.
 */
(function () {
  'use strict';

  const LEFT = '20px';
  const Z_INDEX = '2147483647';
  const MOBILE_BREAKPOINT = 749;
  const BASE_BOTTOM = 20;
  const MAX_BOTTOM = 80;

  // Body classes that indicate a full-screen drawer is open over the page.
  // Do NOT include 'scroll-locked' — that's used by many small modals and
  // would hide the chat in contexts where it should stay visible.
  const DRAWER_CLASSES = ['cart-drawer-open', 'nf-drawer-open'];
  function isDrawerOpen() {
    const cls = document.body.classList;
    for (let i = 0; i < DRAWER_CLASSES.length; i++) {
      if (cls.contains(DRAWER_CLASSES[i])) return true;
    }
    return false;
  }

  function currentBottom() {
    if (window.innerWidth > MOBILE_BREAKPOINT) return BASE_BOTTOM + 'px';
    const footer = document.querySelector('footer.footer') || document.querySelector('footer');
    if (!footer) return BASE_BOTTOM + 'px';
    const rect = footer.getBoundingClientRect();
    const encroach = Math.max(0, window.innerHeight - rect.top);
    return Math.min(MAX_BOTTOM, BASE_BOTTOM + encroach) + 'px';
  }

  function applyFix() {
    const inbox = document.querySelector('inbox-online-store-chat');
    if (!inbox) return;

    // Reparent once — move #shopify-chat to top of body so it escapes
    // stacking contexts that would otherwise clip it under other content.
    const parent = inbox.parentElement;
    if (parent && parent.id === 'shopify-chat' && !parent.hasAttribute('data-reparented')) {
      parent.setAttribute('data-reparented', 'true');
      document.body.insertBefore(parent, document.body.firstChild);
      parent.style.setProperty('position', 'fixed', 'important');
      parent.style.setProperty('inset', '0', 'important');
      parent.style.setProperty('z-index', Z_INDEX, 'important');
      parent.style.setProperty('pointer-events', 'none', 'important');
      parent.style.setProperty('isolation', 'isolate', 'important');
    }

    const bottom = currentBottom();

    // Inject :host position overrides into the widget's shadow DOM. The widget's
    // own shadow styles use :host{top:X} which sit at the same specificity layer
    // as inline host styles — adding a rule INSIDE the shadow DOM ensures ours
    // is the last-declared and wins the cascade.
    if (inbox.shadowRoot) {
      let styleEl = inbox.shadowRoot.getElementById('dyc-position-override');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'dyc-position-override';
        inbox.shadowRoot.insertBefore(styleEl, inbox.shadowRoot.firstChild);
      }
      const wanted =
        ':host{position:fixed!important;bottom:' + bottom +
        '!important;left:' + LEFT +
        '!important;top:auto!important;right:auto!important;z-index:' + Z_INDEX + '!important}';
      if (styleEl.textContent !== wanted) styleEl.textContent = wanted;
    }

    // Position — safe to re-apply; these properties don't loop with any observer
    inbox.style.setProperty('position', 'fixed', 'important');
    inbox.style.setProperty('bottom', bottom, 'important');
    inbox.style.setProperty('left', LEFT, 'important');
    inbox.style.setProperty('right', 'auto', 'important');
    inbox.style.setProperty('top', 'auto', 'important');
    inbox.style.setProperty('z-index', Z_INDEX, 'important');
    inbox.style.removeProperty('transform');
    inbox.style.removeProperty('inset');

    // Visibility: when a drawer is open we REMOVE our inline overrides. This lets
    // the stylesheet rules in nav-finder.css and cart-drawer.js (display:none
    // !important) win, because inline !important would otherwise out-specify CSS.
    if (isDrawerOpen()) {
      inbox.style.removeProperty('display');
      inbox.style.removeProperty('visibility');
      inbox.style.removeProperty('opacity');
      inbox.style.removeProperty('pointer-events');
    } else {
      inbox.style.setProperty('display', 'block', 'important');
      inbox.style.setProperty('visibility', 'visible', 'important');
      inbox.style.setProperty('opacity', '1', 'important');
      inbox.style.setProperty('pointer-events', 'auto', 'important');
    }

    if (!inbox.getAttribute('button-position')) {
      inbox.setAttribute('button-position', 'bottom_left');
    }
    if (!inbox.getAttribute('button-vertical-position')) {
      inbox.setAttribute('button-vertical-position', 'lowest');
    }
  }

  // RAF-throttle: at most one applyFix per animation frame. Coalesces rapid
  // triggers (many body class mutations, scroll events) into one DOM write.
  let pending = false;
  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      applyFix();
    });
  }

  // Initial + spaced retries in case the widget loads late
  schedule();
  [250, 750, 1500, 3000].forEach(function (ms) { setTimeout(schedule, ms); });

  // Widget added to DOM — only observe shallow children of body, not subtree,
  // so we don't fire on every descendant mutation
  new MutationObserver(schedule).observe(document.body, { childList: true });

  // Drawer open/close — body class is the single source of truth
  new MutationObserver(schedule).observe(document.body, {
    attributes: true,
    attributeFilter: ['class']
  });

  window.addEventListener('load', schedule);
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('scroll', schedule, { passive: true });
})();
